import {HttpErrorResponse, HttpResponse} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {Router} from '@angular/router';
import {Actions, createEffect, ofType} from '@ngrx/effects';
import * as Sentry from '@sentry/angular-ivy';
import {Observable, of} from 'rxjs';
import {catchError, map, switchMap, tap} from 'rxjs/operators';
import {LoginResponse} from 'src/app/shared/models/fusion-auth';
import {FusionAuthService} from '../../shared/services/fusion-auth';
import {StorageService} from '../../shared/services/general';
import {FusionAuthActionTypes, LogIn, LogInFailure, LogInSuccess} from '../actions/fusion-auth.actions';
import {SentryBreadcrumbCategory} from "../../../enum/sentry-breadcrumb-category";

@Injectable({
  providedIn: 'root',
})
export class FusionAuthEffects {

  public logIn: Observable<LogInFailure | LogInSuccess> = createEffect(() => this.actions.pipe(
    ofType(FusionAuthActionTypes.LOGIN),
    map((action: LogIn) => action.payload),
    switchMap(payload => {
      return this.fusionAuthService.login(payload).pipe(
        map((response: HttpResponse<LoginResponse>) => {
          return new LogInSuccess(response.body as LoginResponse);
        }),
        catchError((error: HttpErrorResponse) => {
          return of(new LogInFailure({ error }));
        }),
      );
    }),
  ));

  public logInSuccess: Observable<LogInSuccess> = createEffect(() => this.actions.pipe(
    ofType(FusionAuthActionTypes.LOGIN_SUCCESS),
    tap(async () => {
      Sentry.configureScope((scope) => {
        const value = this.storage.get('user', 'auth');
        if (value) {
          const user = JSON.parse(value);

          scope.setUser({
            id: user.id,
            email: user.email
          });

          Sentry.setContext('user-data', {
            data: user.data,
            registrations: JSON.stringify(user.registrations)
          });
        }
      });
      if (this.fusionAuthService.IsLoggedIn === 'true') {
        if (this.fusionAuthService.Profile.registrations.filter(r => r.roles.includes('Patient')).length > 0)
          await this.router.navigate(['/secured/billing'], {queryParams: {isUserLogged: true}});
        else
          await this.router.navigate(['/secured/results/lab'], {queryParams: {isUserLogged: true}});
        return;
      }
    }),
  ), {dispatch: false});


  public logInFailure: Observable<HttpErrorResponse> = createEffect(() => this.actions.pipe(
    ofType(FusionAuthActionTypes.LOGIN_FAILURE),
    tap((error: HttpErrorResponse) => {
      Sentry.addBreadcrumb({
        category: SentryBreadcrumbCategory.config,
        message: 'There was an error logging out the user.',
        level: 'error',
      });
      Sentry.captureException(error.error);
    }),
  ), {dispatch: false});


  public logOut: Observable<any> = createEffect(() => this.actions.pipe(
    ofType(FusionAuthActionTypes.LOGOUT),
    tap(() => {
      return this.fusionAuthService.logout();
    }),
    tap(() => this.router.navigateByUrl('/login')),
  ), {dispatch: false});

  constructor(
    private fusionAuthService: FusionAuthService,
    private actions: Actions,
    private storage: StorageService,
    private router: Router,
  ) { }
}
